/**
 * Versioning & Snapshot System
 *
 * Provides a unified version history model across Quotes, Contracts,
 * Change Orders, and Jobs. Every commercial state change creates a
 * frozen snapshot that preserves build config, pricing, scope, and
 * metadata for full traceability.
 */

import type { ProjectConfiguration } from "./types";
import type { ContractDocument } from "./quoteTypes";
import type { ChangeOrder } from "./changeOrderTypes";
import { createConfigSnapshot } from "./buildConfigMapping";

/* ═══════════════════════════════════════════════════
   Core version record
   ═══════════════════════════════════════════════════ */

export type VersionRecordType = "quote" | "contract" | "change_order" | "job_update";

export interface VersionRecord {
  id: string;
  version: number;
  type: VersionRecordType;
  /** Human-readable label e.g. "Original Quote", "Change Order #2" */
  label: string;
  /** What changed in this version */
  summary: string;
  /** Frozen build configuration */
  configSnapshot: ProjectConfiguration;
  configHash: string;
  /** Pricing at this version */
  contractValue: number;
  previousValue?: number;
  delta?: number;
  /** Status */
  status: "draft" | "active" | "signed" | "superseded" | "voided";
  /** Who and when */
  createdAt: string;
  createdBy: string;
  signedAt?: string;
  signedBy?: string;
  /** Reference IDs */
  sourceQuoteId?: string;
  contractId?: string;
  changeOrderId?: string;
  jobId?: string;
}

/* ═══════════════════════════════════════════════════
   Version lineage — attached to a Quote or Job
   ═══════════════════════════════════════════════════ */

export interface VersionLineage {
  /** All versions in chronological order */
  versions: VersionRecord[];
  /** Index of the current active/approved version */
  activeVersionIndex: number;
}

export function createEmptyLineage(): VersionLineage {
  return { versions: [], activeVersionIndex: -1 };
}

/* ═══════════════════════════════════════════════════
   Version creation helpers
   ═══════════════════════════════════════════════════ */

let _versionCounter = 0;
function nextVersionId(): string {
  _versionCounter++;
  return `VER-${Date.now().toString(36)}-${_versionCounter}`;
}

/**
 * Creates version record when a Quote is first configured.
 */
export function createQuoteVersion(
  config: ProjectConfiguration,
  contractValue: number,
  quoteId: string,
  createdBy: string,
  existingVersions: VersionRecord[] = [],
): VersionRecord {
  const snapshot = createConfigSnapshot(config);
  const version = existingVersions.length + 1;
  return {
    id: nextVersionId(),
    version,
    type: "quote",
    label: version === 1 ? "Original Quote" : `Quote Revision ${version}`,
    summary: version === 1 ? "Initial project configuration and pricing" : "Quote revised before signing",
    configSnapshot: snapshot.config,
    configHash: snapshot.hash,
    contractValue,
    previousValue: existingVersions.length > 0 ? existingVersions[existingVersions.length - 1].contractValue : undefined,
    delta: existingVersions.length > 0 ? contractValue - existingVersions[existingVersions.length - 1].contractValue : undefined,
    status: "draft",
    createdAt: new Date().toISOString(),
    createdBy,
    sourceQuoteId: quoteId,
  };
}

/**
 * Creates version record when a Contract is signed.
 * Supersedes the previous quote version.
 */
export function createContractVersion(
  config: ProjectConfiguration,
  contract: ContractDocument,
  quoteId: string,
  createdBy: string,
  existingVersions: VersionRecord[] = [],
): VersionRecord {
  const snapshot = createConfigSnapshot(config);
  const version = existingVersions.length + 1;

  // Mark all previous versions as superseded
  existingVersions.forEach(v => {
    if (v.status === "draft" || v.status === "active") {
      v.status = "superseded";
    }
  });

  return {
    id: nextVersionId(),
    version,
    type: "contract",
    label: "Signed Contract",
    summary: `Contract signed — ${contract.paymentMethod || "Standard"} · $${contract.contractTotal.toLocaleString()}`,
    configSnapshot: snapshot.config,
    configHash: snapshot.hash,
    contractValue: contract.contractTotal,
    previousValue: existingVersions.length > 0 ? existingVersions[existingVersions.length - 1].contractValue : undefined,
    delta: existingVersions.length > 0 ? contract.contractTotal - existingVersions[existingVersions.length - 1].contractValue : 0,
    status: "signed",
    createdAt: contract.createdAt,
    createdBy,
    signedAt: contract.approvedAt,
    signedBy: createdBy,
    sourceQuoteId: quoteId,
    contractId: contract.id,
  };
}

/**
 * Creates version record when a Change Order is signed.
 */
export function createChangeOrderVersion(
  changeOrder: ChangeOrder,
  config: ProjectConfiguration,
  existingVersions: VersionRecord[] = [],
): VersionRecord {
  const snapshot = createConfigSnapshot(config);
  const version = existingVersions.length + 1;

  // Mark previous signed/active as superseded (they become historical)
  existingVersions.forEach(v => {
    if (v.status === "signed" || v.status === "active") {
      v.status = "superseded";
    }
  });

  return {
    id: nextVersionId(),
    version,
    type: "change_order",
    label: `Change Order #${changeOrder.orderNumber}`,
    summary: changeOrder.summary,
    configSnapshot: snapshot.config,
    configHash: snapshot.hash,
    contractValue: changeOrder.revisedContractValue,
    previousValue: changeOrder.originalContractValue,
    delta: changeOrder.changeAmount,
    status: changeOrder.status === "Signed" ? "signed" : "draft",
    createdAt: changeOrder.createdAt,
    createdBy: changeOrder.createdBy,
    signedAt: changeOrder.signedAt,
    changeOrderId: changeOrder.id,
    jobId: changeOrder.jobId,
  };
}

/* ═══════════════════════════════════════════════════
   Lineage helpers
   ═══════════════════════════════════════════════════ */

/**
 * Gets the current active/approved version from a lineage.
 */
export function getActiveVersion(lineage: VersionLineage): VersionRecord | null {
  if (lineage.activeVersionIndex >= 0 && lineage.activeVersionIndex < lineage.versions.length) {
    return lineage.versions[lineage.activeVersionIndex];
  }
  // Fallback: find latest signed or active
  for (let i = lineage.versions.length - 1; i >= 0; i--) {
    if (lineage.versions[i].status === "signed" || lineage.versions[i].status === "active") {
      return lineage.versions[i];
    }
  }
  return lineage.versions.length > 0 ? lineage.versions[lineage.versions.length - 1] : null;
}

/**
 * Gets the original (first) signed version.
 */
export function getOriginalVersion(lineage: VersionLineage): VersionRecord | null {
  return lineage.versions.find(v => v.type === "contract" && (v.status === "signed" || v.status === "superseded")) || null;
}

/**
 * Adds a version to the lineage and updates the active index.
 */
export function addVersion(lineage: VersionLineage, record: VersionRecord): VersionLineage {
  const versions = [...lineage.versions, record];
  const activeVersionIndex = record.status === "signed" || record.status === "active"
    ? versions.length - 1
    : lineage.activeVersionIndex;
  return { versions, activeVersionIndex };
}

/* ═══════════════════════════════════════════════════
   Build version lineage from existing data
   (reconstructs history from Quote + Job records)
   ═══════════════════════════════════════════════════ */

export function buildLineageFromRecords(
  quoteId: string,
  config: ProjectConfiguration | undefined,
  contractValue: number,
  contract: ContractDocument | undefined,
  changeOrders: ChangeOrder[] = [],
  createdBy: string = "System",
): VersionLineage {
  const versions: VersionRecord[] = [];

  // 1. Quote version
  if (config) {
    versions.push(createQuoteVersion(config, contractValue, quoteId, createdBy));
  }

  // 2. Contract version
  if (contract && config) {
    versions.push(createContractVersion(config, contract, quoteId, createdBy, [...versions]));
  }

  // 3. Change order versions
  const signedCOs = changeOrders
    .filter(co => co.status === "Signed")
    .sort((a, b) => a.orderNumber - b.orderNumber);

  for (const co of signedCOs) {
    const coConfig = co.revisedConfig || config;
    if (coConfig) {
      versions.push(createChangeOrderVersion(co, coConfig, [...versions]));
    }
  }

  // Find active version (latest signed)
  let activeVersionIndex = -1;
  for (let i = versions.length - 1; i >= 0; i--) {
    if (versions[i].status === "signed") {
      activeVersionIndex = i;
      break;
    }
  }

  return { versions, activeVersionIndex };
}

/**
 * Checks if the current config has drifted from the last signed version.
 */
export function hasConfigDrift(lineage: VersionLineage, currentConfig: ProjectConfiguration): boolean {
  const active = getActiveVersion(lineage);
  if (!active) return false;
  const currentSnapshot = createConfigSnapshot(currentConfig);
  return currentSnapshot.hash !== active.configHash;
}
